/**
 * Mirrors backend `apps.users.permission_contract.PAGE_PATH_VIEW_ALTERNATIVES`.
 * Keep in sync when adding routes or permission codes.
 */
import { ALL_CATALOG_PATHS } from './navCatalog';

export const PAGE_PATH_VIEW_ALTERNATIVES: Record<string, readonly string[]> = {
  '/dashboard': ['dashboard_view', 'dashboard_admin'],
  '/delivery-calendar': [
    'jobs_view',
    'delivery_calendar_view',
    'delivery_calendar_manage',
    'delivery_manage',
    'production_calendar_view',
    'production_calendar_manage',
  ],
  '/holiday-calendar': ['holidays_view', 'holidays_manage', 'calendar_view'],
  '/users': ['users_view', 'users_create', 'users_edit', 'users_delete'],
  '/roles': ['roles_view', 'roles_create', 'roles_edit', 'roles_delete', 'users_view'],
  '/jobs': [
    'jobs_view',
    'jobs_create',
    'jobs_edit',
    'delivery_calendar_view',
    'delivery_calendar_manage',
    'production_calendar_view',
    'production_calendar_manage',
    'delivery_manage',
  ],
  '/quotes': ['quotes_view', 'quotes_create', 'quotes_edit', 'quotes_delete'],
  '/pallet-management': ['jobs_view', 'production_view', 'production_manage'],
  '/cartlinks': ['cartlinks_view', 'cartlinks_manage'],
  '/delivery-management': [
    'jobs_view',
    'delivery_view',
    'delivery_manage',
    'delivery_calendar_manage',
  ],
  '/delivery-postcode-management': ['delivery_view', 'delivery_manage'],
  '/production': ['jobs_view', 'production_view', 'production_manage'],
  '/production-line': [
    'jobs_view',
    'production_line_view',
    'production_line_manage',
    'production_manage',
  ],
  '/glass-arrival-jobs': ['jobs_view', 'glass_arrival_view', 'glass_arrival_manage'],
  '/delivery-page': [
    'jobs_view',
    'delivery_view',
    'delivery_manage',
    'orders_view',
    'orders_edit',
    'orders_delete',
  ],
  '/glass-values': [
    'glass_values_view',
    'glass_values_create',
    'glass_values_edit',
    'glass_values_delete',
  ],
  '/production-dashboard': [
    'jobs_view',
    'production_dashboard_view',
    'production_view',
    'production_manage',
  ],
  '/holidays': ['holidays_view', 'holidays_manage'],
  '/overtime': ['overtime_view', 'overtime_manage'],
  '/orders': [
    'orders_view',
    'orders_create',
    'orders_edit',
    'orders_delete',
    'delivery_view',
    'delivery_manage',
  ],
  '/delivery': ['jobs_view', 'delivery_view', 'delivery_manage'],
  '/test/pdf': ['settings_view', 'settings_edit', 'reports_view', 'dashboard_admin'],
};

/** Cached set of all permission codes used in the page↔permission contract. */
const CONTRACT_PERMISSION_CODES = new Set(Object.values(PAGE_PATH_VIEW_ALTERNATIVES).flat());

export function getContractPermissionCodes(): ReadonlySet<string> {
  return CONTRACT_PERMISSION_CODES;
}

let permissionCodeToPagePathsCache: ReadonlyMap<string, readonly string[]> | null = null;

/**
 * For each permission code, catalog paths whose contract includes that code.
 * Used for “shared across pages” hints in the role editor.
 */
export function getPermissionCodeToPagePaths(): ReadonlyMap<string, readonly string[]> {
  if (!permissionCodeToPagePathsCache) {
    const m = new Map<string, string[]>();
    for (const [path, codes] of Object.entries(PAGE_PATH_VIEW_ALTERNATIVES)) {
      for (const code of codes) {
        let paths = m.get(code);
        if (!paths) {
          paths = [];
          m.set(code, paths);
        }
        if (!paths.includes(path)) paths.push(path);
      }
    }
    for (const paths of m.values()) {
      paths.sort((a, b) => a.localeCompare(b));
    }
    permissionCodeToPagePathsCache = m;
  }
  return permissionCodeToPagePathsCache;
}

const PREFIXES_LONGEST_FIRST = [...ALL_CATALOG_PATHS].sort((a, b) => b.length - a.length);

/** Longest-prefix match against the nav catalog. */
export function matchingPagePrefix(pathname: string): string | null {
  const hit = PREFIXES_LONGEST_FIRST.find((p) => pathname === p || pathname.startsWith(`${p}/`));
  return hit ?? null;
}

/** True if any permission code grants access to this path (for previews / tooling). */
export function pathVisibleWithPermissions(
  pathname: string,
  permissionCodes: readonly string[],
): boolean {
  const prefix = matchingPagePrefix(pathname);
  if (!prefix) return false;
  const alts = PAGE_PATH_VIEW_ALTERNATIVES[prefix];
  if (!alts?.length) return false;
  const set = new Set(permissionCodes);
  return alts.some((c) => set.has(c));
}
